// ─────────────────────────────────────────────────────────────────────────
// Demo / reviewer data — UI suppression list (single source of truth)
// ─────────────────────────────────────────────────────────────────────────
//
// The "Government Hostel - 1" entity and everything hanging off it exist in
// the PRODUCTION database ONLY so Play Store / App Store reviewers can log in
// (with the demo FO accounts) and exercise the mobile app. The backend
// keeps serving these records — the mobile client needs them.
//
// This file lists the demo entity + every dependent id so the WEB admin app
// can hide them everywhere it controls: lists, pickers, detail deep-links,
// client-side search, and any count the frontend derives from a list it
// fetches in full. Every consumer imports from here — never re-declare these
// ids in a feature folder.
//
// What this does NOT cover: counts that arrive pre-aggregated from a server
// stats endpoint with no per-item breakdown (most collector-dashboard KPI
// cards, HOD bucket totals). Those stay slightly inflated until the backend
// `DEMO_ENTITY_ID` exclusion lands — tracked separately on the backend.
//
// Snapshot: 2026-06-12 (after prod cleanup, before Yadadri reseed). Ids are
// stable until the next reseed. AFTER A RESEED, refresh every set below.
// ─────────────────────────────────────────────────────────────────────────

/**
 * Entity-level hide list. Includes the demo entity plus any other entity a
 * stakeholder has asked to suppress from the live web app. Entity lists,
 * selectors and KPI math all read from this set.
 */
export const HIDDEN_ENTITY_IDS: ReadonlySet<string> = new Set([
  // Stakeholder-hidden (pre-existing, not part of the demo bundle).
  '75e376ed-5568-4f10-9a48-f476c1832e01',
  // Demo entity — "Government Hostel - 1" (short_code GHO).
  '8798d74c-a56d-4e36-8957-49ac2dd7a517',
])

/** Demo departments (the demo entity's "Health" department). */
export const DEMO_DEPARTMENT_IDS: ReadonlySet<string> = new Set([
  '7b88c509-ce28-489b-b069-d6204a1924c1',
])

/** Demo sites (4 — 3 active, 1 inactive). */
export const DEMO_SITE_IDS: ReadonlySet<string> = new Set([
  'ff83057c-b4ce-4c2e-877d-436bd3a1dd4f',
  '29d385ed-8ee6-47ac-90ae-f0b662d4610d',
  'b505605a-333e-4f98-98d5-56af1ad0e62f',
  'e92adaae-0067-4174-bb91-fd8cd4fb39a1',
])

/** Demo form templates (3). */
export const DEMO_TEMPLATE_IDS: ReadonlySet<string> = new Set([
  'aa000001-0000-4000-8000-000000000001',
  'aa000002-0000-4000-8000-000000000002',
  'aa000003-0000-4000-8000-000000000003',
])

/** Demo assignments / tasks (3). */
export const DEMO_ASSIGNMENT_IDS: ReadonlySet<string> = new Set([
  'e7a4271a-76af-4674-a8f8-3daa9f50de99',
  '1ed9bb25-6a7a-468d-beb1-186f55b2f4cc',
  '0fa3497d-b616-4c67-a9f9-96cf445a4d91',
])

/** Demo survey schedules (2). */
export const DEMO_SCHEDULE_IDS: ReadonlySet<string> = new Set([
  'c064469b-267e-4546-b190-3edb68b2ca5c',
  '308ff0f5-33dd-4291-8644-6d3def4b748c',
])

/** Demo issues (4 — all on site b505605a). */
export const DEMO_ISSUE_IDS: ReadonlySet<string> = new Set([
  '3ee8f19d-66bf-4b57-a76c-0b816dadeef9',
  '127f1e49-af76-4dea-a20d-01594bbf50a6',
  '8740fd7f-9e0d-4c86-aebd-37752910446f',
  'b6daeb78-3cfa-4fe0-b530-171543a256ff',
])

/**
 * Demo user accounts. The FO ones MUST stay functional on the backend
 * (reviewers log in with them) — we only hide them from the admin user list,
 * counts and pickers. The legacy site-rep id stays listed defensively in
 * case the record lingers in older data.
 */
export const DEMO_USER_IDS: ReadonlySet<string> = new Set([
  'ad7fc06e-eba1-4f0d-a34a-8084fe06fa2a', // Uday - App Field officer (FO)
  'aa000004-0000-4000-8000-000000000004', // app.siterep (legacy site-rep account)
  'f78d1205-af3b-445f-b8ae-8b7e8a6464a3', // Uday - App Field officer_1 (FO)
  '94be155d-115f-4755-846a-9dbb74d487bd', // test officer (FO)
])

// ── Environment gate ───────────────────────────────────────────────────────
// The demo/reviewer bundle lives in the PRODUCTION database only, so this
// suppression is meaningful only on production builds. Dev / stage / local point
// at other databases where these same records are the real test data — hiding
// them there blanks out the GIS map markers, issue lists and counts. A build
// reveals the data by setting `VITE_SHOW_DEMO_DATA=true`; unset / any other value
// keeps suppression on (so production stays clean by default, even if the flag is
// forgotten). Bracket access mirrors env-config.ts and avoids a typed-env decl.
const SHOW_DEMO_DATA = import.meta.env['VITE_SHOW_DEMO_DATA'] === 'true'

// ── Predicates ─────────────────────────────────────────────────────────────
// Null-safe by design: ids arrive from loosely-typed API payloads and route
// params, so an `undefined` id simply means "not a demo record". When
// `SHOW_DEMO_DATA` is on (non-prod), every predicate returns false — nothing is
// suppressed, so all feeds (markers, lists, counts) consistently show the data.
const suppressed = (set: ReadonlySet<string>, id?: string | null): boolean =>
  !SHOW_DEMO_DATA && !!id && set.has(id)

export const isHiddenEntity = (id?: string | null): boolean =>
  suppressed(HIDDEN_ENTITY_IDS, id)

export const isDemoDepartment = (id?: string | null): boolean =>
  suppressed(DEMO_DEPARTMENT_IDS, id)

export const isDemoSite = (id?: string | null): boolean =>
  suppressed(DEMO_SITE_IDS, id)

export const isDemoTemplate = (id?: string | null): boolean =>
  suppressed(DEMO_TEMPLATE_IDS, id)

export const isDemoAssignment = (id?: string | null): boolean =>
  suppressed(DEMO_ASSIGNMENT_IDS, id)

export const isDemoSchedule = (id?: string | null): boolean =>
  suppressed(DEMO_SCHEDULE_IDS, id)

export const isDemoIssue = (id?: string | null): boolean =>
  suppressed(DEMO_ISSUE_IDS, id)

export const isDemoUser = (id?: string | null): boolean =>
  suppressed(DEMO_USER_IDS, id)
